import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Shield,
  Clock,
  Key,
  Lock,
  Loader2,
  Save,
  RotateCcw,
  AlertCircle
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { securitySettingsAPI } from '../../api/securitySettings';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const SecuritySettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    session_timeout_minutes: 120,
    require_password_change: false,
    password_change_days: 90,
    require_2fa: false
  });
  const [originalSettings, setOriginalSettings] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await securitySettingsAPI.getSettings();
      if (response.success && response.data) {
        const data = {
          session_timeout_minutes: response.data.session_timeout_minutes ?? 120,
          require_password_change: response.data.require_password_change ?? false,
          password_change_days: response.data.password_change_days ?? 90,
          require_2fa: response.data.require_2fa ?? false
        };
        setSettings(data);
        setOriginalSettings(data);
      }
    } catch (error) {
      console.error('Error fetching security settings:', error);
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const response = await securitySettingsAPI.updateSettings(settings);
      if (response.success) {
        setOriginalSettings({ ...settings });
        toast.success('Security settings saved successfully');
      } else {
        toast.error(response.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving security settings:', error);
      toast.error(error.response?.data?.error || 'Failed to save security settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (originalSettings) {
      setSettings({ ...originalSettings });
    }
  };

  const hasChanges =
    originalSettings && JSON.stringify(settings) !== JSON.stringify(originalSettings);

  // Only super_admin / system_admin can access
  if (user?.role !== 'super_admin' && user?.role !== 'system_admin') {
    return (
      <div className="min-h-screen bg-secondary-offWhite p-6 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-primary-black mb-2">Access Denied</h2>
          <p className="text-secondary-gray">Only Super Admin can access security settings.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  return (
    <div className="min-h-screen bg-secondary-offWhite p-4 md:p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-primary-black">Security Settings</h1>
              <p className="text-secondary-gray text-sm mt-1">
                Authentication and session policies
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave}>
          {/* Session Timeout */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-primary-black">Session Timeout</h2>
                <p className="text-secondary-gray text-sm">Auto logout after inactivity</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary-black mb-2">
                Timeout Duration (minutes)
              </label>
              <input
                type="number"
                min="5"
                max="1440"
                value={settings.session_timeout_minutes}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    session_timeout_minutes: parseInt(e.target.value) || 120
                  })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold focus:border-transparent"
              />
              <p className="text-xs text-secondary-gray mt-2">
                Users will be automatically logged out after this many minutes of inactivity. Min:
                5, Max: 1440 (24 hours).
              </p>
            </div>
          </div>

          {/* Require Password Change */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Key className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-primary-black">
                    Require Password Change
                  </h2>
                  <p className="text-secondary-gray text-sm">
                    Force users to change password periodically
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.require_password_change}
                  onChange={(e) =>
                    setSettings({ ...settings, require_password_change: e.target.checked })
                  }
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-accent-gold rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-gold"></div>
              </label>
            </div>
            {settings.require_password_change && (
              <div className="mt-4 ml-12">
                <label className="block text-sm font-medium text-primary-black mb-2">
                  Change Every (days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={settings.password_change_days}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      password_change_days: parseInt(e.target.value) || 90
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-gold focus:border-transparent"
                />
                <p className="text-xs text-secondary-gray mt-2">
                  Users will be prompted to change their password after this many days.
                </p>
              </div>
            )}
          </div>

          {/* Two-Factor Authentication */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Lock className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-primary-black">
                    Two-Factor Authentication
                  </h2>
                  <p className="text-secondary-gray text-sm">Require 2FA for all users</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.require_2fa}
                  onChange={(e) => setSettings({ ...settings, require_2fa: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-accent-gold rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-gold"></div>
              </label>
            </div>
            {settings.require_2fa && (
              <div className="mt-4 ml-12 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  2FA enforcement is not yet implemented. Enabling this flag will save the
                  preference for future use.
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={handleReset}
              disabled={!hasChanges || saving}
              className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
            <button
              type="submit"
              disabled={!hasChanges || saving}
              className="flex items-center gap-2 px-6 py-3 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SecuritySettingsPage;
